import * as React from 'react';
import * as IoC from 'typescript-ioc';
import { IAlertProps, IAlertsService, IConfirmProps, INotificationProps } from '../../../../infrastructure/alerts';
import { AlertsService } from '../../../../infrastructure/implementations/alerts';
import { AlertType, ISingleAlert, ISingleNotification } from './alert-type';
import './alerts.scss';
import { AlertPopupsContainer } from './components/alert-popups-container';
import { NotificationsContainer } from './components/notifiacations-container';

interface IState {
    // alerts host can work with various kind of popups, therefore let generic be typed as 'any' here
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    visiblePopups: ISingleAlert<any>[];
    visibleNotifications: ISingleNotification[];
}

/**
 * this component hosts alerts, supposed to be triggered using IAlertsService
 * and serve as implementation details of AlertsService.
 */
class AlertsHost extends React.Component<{}, IState> {
    constructor(props, context) {
        super(props, context);

        this.state = {
            visiblePopups: [],
            visibleNotifications: [],
        };
    }

    public componentDidMount(): void {
        const alertsService = IoC.Container.get(IAlertsService) as AlertsService;
        alertsService.setAlertsHost({
            showConfirm: (props: IConfirmProps) => this.showPopup(props, AlertType.confirm),
            showAlert: (props: IAlertProps) => this.showPopup(props, AlertType.alert),
            closeAlert: (alertKey: string) => this.closePopup(alertKey),
            showNotification: (props: INotificationProps) => this.showNotificationComponent(props),
            closeNotification: (alertKey: string) => this.closePopup(alertKey),
            closeEventNotifications: this.closeEventNotifcations,
        });
    }

    private showNotificationComponent = (notificationProps: INotificationProps) => {
        const newNotification = this.createNotification(notificationProps);
        this.updateNotificationList(newNotification);
        return newNotification.alertKey;
    };

    private createNotification = (notificationProps: INotificationProps): ISingleNotification => {
        const { isRelatedToEvent, ...other } = notificationProps;

        return {
            popupProps: other,
            alertKey: (new Date()).getTime().toString(10),
            isRelatedToEvent: isRelatedToEvent
        };
    };

    private updateNotificationList = (newNotification: ISingleNotification): void => {
        this.setState(prevState => ({
            visibleNotifications: [...prevState.visibleNotifications, newNotification]
        }));
    };

    private showPopup = (alertProps: IAlertProps | IConfirmProps, type: AlertType): string => {
        const newPopup = this.createPopup(alertProps, type);
        this.updatePopupList(newPopup);
        return newPopup.alertKey;
    };

    private createPopup = (alertProps: IAlertProps | IConfirmProps, type: AlertType): ISingleAlert<IAlertProps | IConfirmProps> => {
        return {
            type: type,
            popupProps: alertProps,
            alertKey: (new Date()).getTime().toString(10),
        };
    };

    private updatePopupList = (newPopup: ISingleAlert<IAlertProps | IConfirmProps>): void => {
        this.setState(prevState => ({
            visiblePopups: [...prevState.visiblePopups, newPopup]
        }));
    };

    private closePopup = (alertKey?: string): void => {
        // if key is not provided - close top popup
        const popupIndex = this.findPopupIndexByKey(alertKey);
        this.removePopupByIndex(popupIndex);
    };

    private findPopupIndexByKey = (alertKey?: string): number => {
        return alertKey
            ? this.state.visiblePopups.findIndex(x => x.alertKey === alertKey)
            : this.state.visiblePopups.length - 1;
    };

    private removePopupByIndex = (index: number): void => {
        if (index !== -1) {
            this.state.visiblePopups.splice(index, 1);
            this.setState({
                visiblePopups: this.state.visiblePopups.slice(),
            });
        }
    };

    private closeEventNotifcations = () => {
        this.setState({
            visibleNotifications: this.state.visibleNotifications.filter(({ isRelatedToEvent }) => !isRelatedToEvent)
        });
    };

    public render(): React.ReactNode {
        if (!this.state.visiblePopups.length && !this.state.visibleNotifications.length) {
            return null;
        }

        return (
            <>
                <AlertPopupsContainer
                    alertsPopupsList={this.state.visiblePopups}
                    closePopup={this.closePopup}
                />
                <NotificationsContainer
                    notificationsList={this.state.visibleNotifications}
                />
            </>
        );
    }
}

export { AlertsHost };
